/**
 * NegoKanban — Vue Kanban du Centre de négociations (drag & drop par statut).
 * Transitions par glisser-déposer mappées sur les actions backend :
 * accept / reject / escalate / take_ownership.
 */
import React, { useMemo, useState } from 'react';
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import { toast } from 'sonner';
import { User, Clock, ArrowRight, Inbox } from 'lucide-react';
import { STATUS_META, TYPE_META, slaInfo, initialOffer, negotiatedOffer, offerGap, fmtXofCompact, fmtRelative } from './negoMeta';

const COLUMNS = [
  { id: 'open', accent: 'border-t-slate-400' },
  { id: 'pending_internal', accent: 'border-t-blue-500' },
  { id: 'countered', accent: 'border-t-orange-500' },
  { id: 'pending_client', accent: 'border-t-purple-500' },
  { id: 'escalated', accent: 'border-t-amber-500' },
  { id: 'accepted', accent: 'border-t-emerald-500' },
  { id: 'rejected', accent: 'border-t-rose-500' },
];

// Mapping "déposé sur colonne X" -> action backend
const DROP_ACTIONS = {
  accepted: { action: 'accept', confirm: 'Accepter cette négociation ?' },
  rejected: { action: 'reject', confirm: null }, // confirm géré par quickAction
  escalated: { action: 'escalate', confirm: null },
  pending_internal: { action: 'take_ownership', confirm: null, onlyFrom: ['open'] },
};

const NegoCard = ({ nego, onOpen, dragging = false }) => {
  const type = TYPE_META[nego.negotiationType] || TYPE_META.other;
  const TypeIcon = type.icon;
  const sla = slaInfo(nego);
  const gap = offerGap(nego);
  const negOffer = negotiatedOffer(nego);
  return (
    <div
      data-testid={`kanban-card-${nego.id}`}
      onClick={() => onOpen?.(nego.id)}
      className={`cursor-grab rounded-lg border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md ${dragging ? 'rotate-2 shadow-xl ring-2 ring-emerald-400' : ''}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-slate-800">{nego.operationNumber || '—'}</span>
        <span className={`flex h-6 w-6 items-center justify-center rounded-md ${type.bg}`} title={type.label}>
          <TypeIcon className={`h-3.5 w-3.5 ${type.color}`} />
        </span>
      </div>
      <div className="mt-1 truncate text-sm font-medium text-slate-700">{nego.clientName || '—'}</div>
      {(nego.departureCity || nego.arrivalCity) && (
        <div className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-slate-400">
          {nego.departureCity} <ArrowRight className="h-3 w-3 shrink-0" /> {nego.arrivalCity}
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="font-semibold tabular-nums text-slate-800">
          {fmtXofCompact(negOffer ?? initialOffer(nego))}
          {gap != null && gap !== 0 && (
            <span className={`ml-1 font-bold ${gap > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {gap > 0 ? '+' : ''}{fmtXofCompact(gap)}
            </span>
          )}
        </span>
        <span className={`flex items-center gap-1 ${sla.cls}`}><Clock className="h-3 w-3" />{sla.label}</span>
      </div>
      <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-1.5">
        <span className="flex items-center gap-1 truncate text-[11px] text-slate-500">
          <User className="h-3 w-3 shrink-0" /> {nego.assignedSalesName || nego.createdByName || '—'}
        </span>
        <span className="text-[10px] text-slate-400">{fmtRelative(nego.lastActivityAt)}</span>
      </div>
    </div>
  );
};

const DraggableCard = ({ nego, onOpen }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: nego.id, data: { nego } });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className={isDragging ? 'opacity-30' : ''}>
      <NegoCard nego={nego} onOpen={onOpen} />
    </div>
  );
};

const KanbanColumn = ({ column, items, onOpen }) => {
  const meta = STATUS_META[column.id] || {};
  const { setNodeRef, isOver } = useDroppable({ id: column.id });
  return (
    <div
      ref={setNodeRef}
      data-testid={`kanban-col-${column.id}`}
      className={`flex w-[270px] shrink-0 flex-col rounded-xl border border-t-4 bg-slate-50/80 ${column.accent} ${isOver ? 'border-emerald-400 bg-emerald-50/60' : 'border-slate-200'}`}
    >
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-600">{meta.label || column.id}</span>
        <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold tabular-nums text-slate-600 shadow-sm" data-testid={`kanban-count-${column.id}`}>{items.length}</span>
      </div>
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-2.5 pb-3" style={{ maxHeight: 'calc(100vh - 380px)', minHeight: 120 }}>
        {items.length === 0 && (
          <div className="flex flex-col items-center gap-1 py-6 text-slate-300">
            <Inbox className="h-5 w-5" /><span className="text-[11px]">Vide</span>
          </div>
        )}
        {items.map((n) => <DraggableCard key={n.id} nego={n} onOpen={onOpen} />)}
      </div>
    </div>
  );
};

export const NegoKanban = ({ rows, loading, onOpen, onAction }) => {
  const [activeNego, setActiveNego] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c.id, []]));
    rows.forEach((n) => {
      const key = g[n.status] ? n.status : (['expired', 'cancelled'].includes(n.status) ? 'rejected' : 'open');
      g[key].push(n);
    });
    return g;
  }, [rows]);

  const handleDragEnd = ({ active, over }) => {
    setActiveNego(null);
    if (!over) return;
    const nego = active.data?.current?.nego;
    const target = over.id;
    if (!nego || nego.status === target) return;

    const rule = DROP_ACTIONS[target];
    if (!rule || (rule.onlyFrom && !rule.onlyFrom.includes(nego.status))) {
      toast.info('Transition non disponible par glisser-déposer — ouvrez le dossier pour agir.');
      return;
    }
    if (rule.confirm && !window.confirm(rule.confirm)) return;
    onAction?.(nego, rule.action);
  };

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="nego-kanban-loading">
        {COLUMNS.map((c) => <div key={c.id} className="h-64 w-[270px] shrink-0 animate-pulse rounded-xl bg-slate-100" />)}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => setActiveNego(active.data?.current?.nego || null)}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveNego(null)}
    >
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="nego-kanban">
        {COLUMNS.map((c) => (
          <KanbanColumn key={c.id} column={c} items={grouped[c.id]} onOpen={onOpen} />
        ))}
      </div>
      <DragOverlay>{activeNego ? <div className="w-[250px]"><NegoCard nego={activeNego} dragging /></div> : null}</DragOverlay>
    </DndContext>
  );
};

export default NegoKanban;
